// 다가오는 일정 조회 — 홈 위젯과 헤더 배지가 함께 쓰는 단일 소스.
//
// 일정 기능이 헤더 아이콘 뒤에 숨어 있어 사용자가 존재 자체를 모르던 문제(발견성)를
// 풀기 위한 훅. 첫 화면에 실제 일정 목록을 바로 보여주고, 헤더에는 오늘 남은 건수를
// 배지로 띄운다. 월간 캘린더(SchedulePanel)는 자체 조회를 유지한다 — 여기서는
// '지금 이후' 짧은 구간만 가볍게 읽는다.
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import type { ScheduleItem } from "@/api/types";
import { dayKey } from "@/components/schedule/utils";

/** 기본 조회 구간 — 오늘 0시부터 30일. */
const DEFAULT_DAYS = 30;

export interface UpcomingSchedules {
  /** 아직 끝나지 않은 일정만, start_at 오름차순(백엔드 정렬 유지). */
  items: ScheduleItem[];
  /** 오늘 날짜에 걸린 일정 중 아직 끝나지 않은 건수 — 헤더 배지용. */
  todayCount: number;
  loading: boolean;
  /** 등록/수정/삭제 후 홈 위젯·배지를 최신화. */
  reload: () => void;
}

export function useUpcomingSchedules(days: number = DEFAULT_DAYS): UpcomingSchedules {
  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 오늘 0시(로컬)
    const to = new Date(from);
    to.setDate(to.getDate() + days);
    setLoading(true);
    api
      .schedules(from.toISOString(), to.toISOString())
      .then((r) => {
        // 오늘 이미 끝난 일정은 '다가오는' 목록에서 뺀다(시작 시각 기준 조회이므로 여기서 걸러야 한다).
        const cutoff = Date.now();
        setItems(r.items.filter((s) => new Date(s.end_at).getTime() >= cutoff));
      })
      // 조회 실패는 조용히 — 일정은 부가 정보이고, 실패 시 위젯이 숨는다.
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    reload();
  }, [reload]);

  const today = dayKey(new Date());
  const todayCount = items.filter((s) => dayKey(new Date(s.start_at)) === today).length;

  return { items, todayCount, loading, reload };
}
